import { Button } from "primereact/button";
import React, { useCallback, useEffect, useState } from "react";
import * as translateService from "../../../localization/translateService";
import NewTrainerList from "./components/NewTrainerList";
import {
  DEFAULT_TRANSLATIONS,
  getTranslationKeysArray,
} from "./constants/smartMatchingOverviewTranslations";
import SmartMatchingModal from "./components/SmartMatchingModal";
import * as localStorageHelper from "../../../helpers/localStorageHelper";
import * as smartMatchingService from "../../../services/smartMatchingService";
import { getBundleDiscounts } from "../../../services/bundleDiscountService";
import { getAllAvailableDirectDeliveryCoupon } from "../../../services/couponService";
export default function SmartMatchingOverview({ props, messages }) {
  const [translations, setTranslations] = useState(DEFAULT_TRANSLATIONS);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentUser, setCurrentUSer] = useState();
  const [showPageData, setShowPageData] = useState(false);
  const [source, setSource] = useState([]);
  const [bundleDiscounts, setBundleDiscounts] = useState([]);
  const [directDeliveryCoupons, setDirectDeliveryCoupons] = useState([]);

  const loadTranslations = useCallback(async () => {
    try {
      const results = await translateService.translateAll(
        getTranslationKeysArray()
      );
      const newTranslations = {};
      Object.keys(DEFAULT_TRANSLATIONS).forEach((k, i) => {
        newTranslations[k] = results[i];
      });
      setTranslations(newTranslations);
    } catch (error) {
      setTranslations(DEFAULT_TRANSLATIONS);
    }
  }, []);

  const loadUserData = useCallback(() => {
    const user = localStorageHelper.getCurrentUserFromLocalStorage();
    setCurrentUSer(user);
    return user;
  }, []);

  const loadDiscounts = useCallback(async () => {
    try {
      const discounts = await getBundleDiscounts();
      setBundleDiscounts(discounts);
      return discounts;
    } catch (error) {
      //console.error("Failed to load bundle discounts:", error);
      return [];
    }
  }, []);

  const loadDirectDeliveryCoupons = useCallback(async () => {
    try {
      const coupons = await getAllAvailableDirectDeliveryCoupon();
      setDirectDeliveryCoupons(coupons);
      return coupons;
    } catch (error) {
      return [];
    }
  }, []);

  const loadSmartMatchingData = useCallback(async (user) => {
    if (!user) return;

    try {
      const smartMatchingData =
        await smartMatchingService.getCurrentSmartMatching();
      if (smartMatchingData && Object.keys(smartMatchingData).length > 0) {
        const initialRequest = {
          first: 0,
          rows: 5,
          filters: null,
        };
        await handlePaging(initialRequest, false);
      }
    } catch (error) {
      //console.error("Failed to load smart matching data:", error);
    }
  }, []);

  useEffect(() => {
    const initializeData = async () => {
      try {
        // Load translations first as they're needed for UI
        await loadTranslations();

        // Load user data first
        const user = loadUserData();

        // Load bundle discounts and smart matching data in parallel if user exists
        const promises = [loadDiscounts()];
        if (user) {
          promises.push(loadDirectDeliveryCoupons());
          promises.push(loadSmartMatchingData(user));
        }

        await Promise.all(promises);
      } catch (error) {
        //console.error("Failed to initialize data:", error);
      }
    };

    initializeData();
  }, [loadTranslations, loadUserData, loadDiscounts, loadSmartMatchingData, loadDirectDeliveryCoupons]);

  const handleOverviewClick = () => {
    if (currentUser) {
      setIsModalOpen(true);
    } else {
      const lang = localStorageHelper.getI18nLanguageFromLocalStorage();
      window.location.href = `/auth/login?lang=${lang}&returnUrl=${window.location.pathname}`;
    }
  };

  const handlePaging = async (request, displayIfEmpty = true) => {
    try {
      const response =
        await smartMatchingService.getCurrentSmartMatchingTrainer(request);

      const trainerList = response.data;
      const translationPromises = [];

      trainerList.forEach((trainer) => {
        if (trainer.trainerLanguages && trainer.trainerLanguages.length > 0) {
          const languageTranslationPromises = trainer.trainerLanguages.map(
            async (lang) => {
              lang.proficiencyDescription = await translateService.translate(
                lang.proficiencyDescription,
                true
              );
              return lang;
            }
          );
          translationPromises.push(Promise.all(languageTranslationPromises));
        }

        if (trainer.trainerTags && trainer.trainerTags.length > 0) {
          const trainerTagTranslationPromises = trainer.trainerTags.map(
            async (tag) => {
              tag.description = await translateService.translate(
                tag.description,
                true
              );
              return tag;
            }
          );
          translationPromises.push(Promise.all(trainerTagTranslationPromises));
        }
      });

      await Promise.all(translationPromises);
      setSource(response);
      if (displayIfEmpty || trainerList.length > 0) {
        setShowPageData(true);
      }
    } catch (error) {
      // handle error
    }
  };

  const handleViewResult = async () => {
    setIsModalOpen(false);
    const initialRequest = {
      first: 0,
      rows: 5,
      filters: null,
    };
    await handlePaging(initialRequest);
  };

  return (
    <div className="flex flex-column w-full smart-matching-overview">
      <div className="flex justify-content-center align-items-center align-self-stretch section-1 flex-column lg:flex-row w-full">
        <div className="left-content animate__animated animate__fadeInLeft">
          <div className="left-content-background">
            <img
              className="image-19 animate__animated animate__pulse animate__infinite animate__delay-2s"
              src={props.smartMatchingImageEllipse19}
              alt=""
            />
            <img
              className="image-20 animate__animated animate__pulse animate__infinite"
              src={props.smartMatchingImageEllipse20}
              alt=""
            />
            <img
              className="image-21 animate__animated animate__pulse animate__infinite animate__delay-2s"
              src={props.smartMatchingImageEllipse21}
              alt=""
            />
            <img
              className="image-22 animate__animated animate__pulse animate__infinite"
              src={props.smartMatchingImageEllipse22}
              alt=""
            />
            <img
              className="left-content-image animate__animated animate__zoomIn"
              src={props.smartMatchingLeftImage}
              alt=""
            />
          </div>
        </div>
        <div className="right-content animate__animated animate__fadeInRight">
          <img
            className="right-content-icon animate__animated animate__bounceIn"
            src={props.smartMatchingRightIcon}
            alt=""
          />
          <div className="flex align-items-center align-self-stretch keduall-typography heading-h3-bold-700 text-default-txt animate__animated animate__fadeInUp flex-wrap">
            {props.smartMatchingTitle.split(" ").map((word, index) => (
              <span key={index} className="gradient-word">
                {word}
              </span>
            ))}
          </div>
          <div className="right-content-description animate__animated animate__fadeInUp animate__delay-1s">
            <span className="right-content-description keduall-typography heading-h2-black-900 text-default-txt">
              🎯
            </span>
            <span> {props.smartMatchingDescription1}</span>
          </div>
          <div className="right-content-description animate__animated animate__fadeInUp animate__delay-2s">
            <span className="right-content-description keduall-typography heading-h2-black-900 text-default-txt">
              🙌
            </span>
            <span> {props.smartMatchingDescription2}</span>
          </div>
          <div className="right-content-description animate__animated animate__fadeInUp animate__delay-3s">
            <span className="right-content-description keduall-typography heading-h2-black-900 text-default-txt">
              ✨
            </span>
            <span> {props.smartMatchingDescription3}</span>
          </div>
          <div>
            <Button
              onClick={handleOverviewClick}
              className="keduall-buttons button-filled-default-large-primary py-3 px-5 bg-buttons-primary text-white text-xl font-semibold line-height-28 border-none cursor-pointer animate__animated animate__fadeInDown"
            >
              {translations.buttonOverviewLabel}
            </Button>
          </div>
        </div>
      </div>
      {/* Show content image when not in data view */}
      {!showPageData && (
        <div className="flex justify-content-center align-items-center section-2 overflow-hidden w-full animate__animated animate__fadeInUp">
          <img
            className="w-full"
            src={props.smartMatchingImageContent}
            alt="smart-matching-banner"
          />
        </div>
      )}
      {/* Show empty state when in data view and no data */}
      {showPageData && (
        <div className="section-3 animate__animated animate__fadeIn">
          <div className="text-center keduall-typography w-full heading-h1-semibold-600 text-primary-txt-color animate__animated animate__fadeInDown">
            {props.smartMatchingResultTitle}
          </div>
          <NewTrainerList
            source={source}
            notFoundImage={props.smartMatchingImageResultNotFound}
            bundleDiscounts={bundleDiscounts}
            props={props}
            onPaging={handlePaging}
            directDeliveryCoupons={directDeliveryCoupons}
            messages={messages}
          />
        </div>
      )}
      <SmartMatchingModal
        props={props}
        isModelOpen={isModalOpen}
        closeModel={() => setIsModalOpen(false)}
        onViewResult={handleViewResult}
      />
    </div>
  );
}
